import { useQuery } from '@tanstack/react-query';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { externalLinks } from 'src/externalLinks';
import {
  fetchAssessmentOptions,
  fetchAssessmentSequenceOptions,
} from 'src/models/assessment/assessment';

import { BaseSelect } from './BaseSelect';

type AssessmentsSequencesSelectProps = {
  name: string;
  label?: ReactNode;
  value: any;
  valueOnly?: boolean;
  onChange?: (value: any) => void;
  multiple?: boolean;
  showManage?: boolean;
};

export const AssessmentsSequencesSelect = ({
  name,
  label,
  value,
  valueOnly = false,
  onChange,
  multiple,
  showManage,
}: AssessmentsSequencesSelectProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'group' });

  const { data: assessments = [] } = useQuery({
    queryKey: ['assessments'],
    queryFn: () => fetchAssessmentOptions(),
  });

  const { data: sequences = [] } = useQuery({
    queryKey: ['assessmentsSequences'],
    queryFn: () => fetchAssessmentSequenceOptions(),
  });

  const assessmentsSequences = useMemo(() => {
    const results: Array<{ type: string; id: string | number; name: string }> =
      [];

    if (assessments?.length) {
      results.push(
        ...assessments.map((a) => ({
          ...a,
          label: a.name,
          type: 'assessment',
        }))
      );
    }

    if (sequences?.length) {
      results.push(
        ...sequences.map((s) => ({
          ...s,
          label: s.name,
          id: s.id.toString(),
          type: 'grouping',
        }))
      );
    }

    return results;
  }, [assessments, sequences]);

  return (
    <Box>
      {label && <Typography>{label}</Typography>}
      <BaseSelect
        name={name}
        value={value}
        options={assessmentsSequences as any}
        groupBy={(option) =>
          option.type === 'assessment'
            ? t('settings.Assessment')
            : t('settings.Assignment Sequence')
        }
        placeholder={t('settings.Select Assessment')}
        multiple={multiple}
        valueOnly={valueOnly}
        onChange={(v) => {
          onChange?.(v);
        }}
      />
      {showManage && (
        <Stack direction="row" spacing={1}>
          <Box component={Link} href={externalLinks.assessmentsPage()}>
            {t('settings.Manage Assessments')}
          </Box>
          <Typography>|</Typography>
          <Box component={Link} href={externalLinks.sequencesPage()}>
            {t('settings.Manage Sequences')}
          </Box>
        </Stack>
      )}
    </Box>
  );
};
